/**
 * Gate0Qualifier.tsx — TICKET-3
 *
 * Reusable two-step Gate 0 qualifier component.
 *
 * Step 1: Is there a transactional event driving this deal?
 *   Yes → Step 2
 *   No  → Not AIP-eligible state
 *
 * Step 2: Is this new business or an existing customer?
 *   New Business  → customerPath = "full"
 *   Existing      → customerPath = "fastlane"
 *
 * Brand: cream bg #FAFAF7, ink text #0B0B0F, red #DC2626 for ineligible state.
 * Font: DM Sans / Inter. No exclamation points, no emojis.
 */

import { useState } from "react";

// ── Types ──────────────────────────────────────────────────────────────────────

export type G0Answer = "yes" | "no";
export type G1Answer = "new" | "existing";
export type CustomerPath = "full" | "fastlane";

export interface Gate0Result {
  eligible: boolean;
  g0Answer: G0Answer;
  g1Answer?: G1Answer;
  customerPath?: CustomerPath;
}

export interface Gate0QualifierProps {
  /** Current saved values (for edit mode). */
  initialG0Answer?: G0Answer | null;
  initialG1Answer?: G1Answer | null;
  /** Called when the user completes both questions. */
  onComplete: (result: Gate0Result) => void;
  /** Called when the user clicks cancel (e.g., dismiss edit). Optional. */
  onCancel?: () => void;
  /** Whether to show a cancel button (for edit-from-scorecard mode). */
  showCancel?: boolean;
  /** Whether this is saving (disables buttons). */
  saving?: boolean;
}

// ── Styles (inline, brand-locked) ─────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: "#FAFAF7",
  border: "1px solid rgba(11,11,15,0.15)",
  borderRadius: 10,
  padding: "24px 28px",
  maxWidth: 520,
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', Inter, system-ui",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(11,11,15,0.55)",
  marginBottom: 8,
};

const questionStyle: React.CSSProperties = {
  fontFamily: "'DM Sans', Inter, system-ui",
  fontWeight: 600,
  fontSize: 15,
  color: "#0B0B0F",
  marginBottom: 16,
};

const btnBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
  padding: "10px 22px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  border: "1.5px solid",
  fontFamily: "Inter, system-ui",
  transition: "background 120ms, color 120ms",
};

const btnActive: React.CSSProperties = {
  ...btnBase,
  background: "#0B0B0F",
  borderColor: "#0B0B0F",
  color: "#FAFAF7",
};

const btnInactive: React.CSSProperties = {
  ...btnBase,
  background: "transparent",
  borderColor: "rgba(11,11,15,0.25)",
  color: "#0B0B0F",
};

const btnDisabled: React.CSSProperties = {
  ...btnBase,
  background: "rgba(11,11,15,0.07)",
  borderColor: "rgba(11,11,15,0.1)",
  color: "rgba(11,11,15,0.4)",
  cursor: "not-allowed",
};

// ── Component ──────────────────────────────────────────────────────────────────

export function Gate0Qualifier({
  initialG0Answer,
  initialG1Answer,
  onComplete,
  onCancel,
  showCancel = false,
  saving = false,
}: Gate0QualifierProps) {
  const [g0, setG0] = useState<G0Answer | null>(initialG0Answer ?? null);
  const [g1, setG1] = useState<G1Answer | null>(initialG1Answer ?? null);

  // Determine current step:
  //   step 1 = g0 not yet answered
  //   step 2 = g0=yes, g1 not yet answered
  //   done   = g0=no OR (g0=yes AND g1 answered)
  const step = !g0 ? 1 : g0 === "no" ? "ineligible" : !g1 ? 2 : "done";

  function handleG0(answer: G0Answer) {
    setG0(answer);
    if (answer === "no") {
      onComplete({ eligible: false, g0Answer: "no" });
    }
    // If switching from "no" back to "yes", reset g1
    if (answer === "yes") {
      setG1(null);
    }
  }

  function handleG1(answer: G1Answer) {
    setG1(answer);
    const customerPath: CustomerPath = answer === "existing" ? "fastlane" : "full";
    onComplete({
      eligible: true,
      g0Answer: "yes",
      g1Answer: answer,
      customerPath,
    });
  }

  return (
    <div style={cardStyle}>
      {/* ── Header ── */}
      <div style={labelStyle}>Pre-qualifier — Qualification</div>

      {/* ── Ineligible banner ── */}
      {step === "ineligible" && (
        <div
          style={{
            background: "rgba(220,38,38,0.06)",
            border: "1px solid rgba(220,38,38,0.25)",
            borderRadius: 6,
            padding: "12px 16px",
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontFamily: "Inter, system-ui",
              fontSize: 13,
              color: "#DC2626",
              fontWeight: 600,
              margin: 0,
            }}
          >
            No transactional event. This account is not AIP-eligible for full
            qualification. Continue developing or close as dead.
          </p>
        </div>
      )}

      {/* ── Step 1: Transactional event ── */}
      <div style={{ marginBottom: step === 1 ? 0 : 20 }}>
        <div style={questionStyle}>
          Is there a transactional event driving this deal?
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            style={
              saving
                ? btnDisabled
                : g0 === "yes"
                ? btnActive
                : btnInactive
            }
            disabled={saving}
            onClick={() => handleG0("yes")}
          >
            Yes
          </button>
          <button
            style={
              saving
                ? btnDisabled
                : g0 === "no"
                ? {
                    ...btnActive,
                    background: "#DC2626",
                    borderColor: "#DC2626",
                  }
                : btnInactive
            }
            disabled={saving}
            onClick={() => handleG0("no")}
          >
            No
          </button>
        </div>
      </div>

      {/* ── Step 2: New vs Existing (only shown when g0=yes) ── */}
      {g0 === "yes" && (
        <div>
          <div style={questionStyle}>
            Is this new business or an existing customer?
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              style={
                saving
                  ? btnDisabled
                  : g1 === "new"
                  ? btnActive
                  : btnInactive
              }
              disabled={saving}
              onClick={() => handleG1("new")}
            >
              New Business
            </button>
            <button
              style={
                saving
                  ? btnDisabled
                  : g1 === "existing"
                  ? btnActive
                  : btnInactive
              }
              disabled={saving}
              onClick={() => handleG1("existing")}
            >
              Existing Customer
            </button>
          </div>

          {g1 === "existing" && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: "rgba(11,11,15,0.05)",
                borderRadius: 6,
                fontFamily: "Inter, system-ui",
                fontSize: 12,
                color: "rgba(11,11,15,0.65)",
              }}
            >
              Existing customer path — CP3, CP4, CP7, and CP8 are skipped.
              Active checkpoints: CP1, CP2, CP5, CP6, CP9, CP10.
            </div>
          )}

          {g1 === "new" && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: "rgba(11,11,15,0.05)",
                borderRadius: 6,
                fontFamily: "Inter, system-ui",
                fontSize: 12,
                color: "rgba(11,11,15,0.65)",
              }}
            >
              New business path — all 10 checkpoints required.
            </div>
          )}
        </div>
      )}

      {/* ── Cancel (edit mode) ── */}
      {showCancel && onCancel && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(11,11,15,0.12)" }}>
          <button
            style={{
              ...btnBase,
              background: "transparent",
              borderColor: "transparent",
              color: "rgba(11,11,15,0.5)",
              fontSize: 12,
              padding: "6px 12px",
            }}
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default Gate0Qualifier;
